/**
 * The six G5 "Test Parameter Defaults" tabs of the test popup (TestMethod_DetailView):
 * their columns (Model.xafml Testparameter_ListView_Test_* views, in G5's order and
 * captions), which rows each tab shows, and the pending ADD / edit buffer that OK sends.
 * Pure — no React — so it is unit-tested on its own.
 */

/** One dbo.Testparameter row as GET /api/v1/test-methods/{id}/parameters returns it. */
export interface ParamRow {
  oid: string;
  parameterId: string | null;
  parameter: string | null;
  qcTypeId: string | null;
  qcType: string | null;
  componentIsDefault: boolean;
  surrogate: boolean;
  internalStandard: boolean;
  hasDefaultResults: boolean;
  [field: string]: unknown;
}

/** A row in a tab: a saved row, or one added with ADD and not saved yet (`pending`). */
export type GridRow = ParamRow & { key: string; pending?: { kind: ParamKind; qcTypeId: string | null } };

/** {id, name} lookup option (units, QC types). */
export interface Option { id: string; name: string | null }

export type ParamKind = 'sample' | 'surrogate' | 'is' | 'qc';
/** How a cell is edited: free text · number · date · Unit dropdown · internal-standard dropdown. */
export type CellKind = 'text' | 'number' | 'date' | 'unit' | 'is';
export interface ParamCol { field: string; header: string; width?: number; edit?: CellKind }

export type TabId = 'sample' | 'surrogate' | 'is' | 'qc' | 'component' | 'qcDefault';

const ro = (field: string, header: string, width = 150): ParamCol => ({ field, header, width });
const tx = (field: string, header: string, width = 90): ParamCol => ({ field, header, width, edit: 'text' });
const num = (field: string, header: string, width = 90): ParamCol => ({ field, header, width, edit: 'number' });
const unit = (field: string, header: string): ParamCol => ({ field, header, width: 110, edit: 'unit' });

// Columns shared by the QC tabs (G5 Testparameter_ListView_Test_QCSampleParameter[_QCParameterDefault]).
const qcLimits: ParamCol[] = [
  tx('defaultResult', 'DefaultResult'), unit('defaultUnits', 'DefaultUnits'),
  num('spikeAmount', 'SpikeAmount'), unit('spikeAmountUnit', 'SpikeAmountUnits'),
  tx('rptLimit', 'RL'), tx('mdl', 'MDL'), tx('rpdHCLimit', 'RPDCtrlLimit'),
  tx('recLCLimit', 'RecLCLimit'), tx('recHCLimit', 'RecUCLimit'),
  tx('lowCLimit', 'LCLimit'), tx('highCLimit', 'UCLimit'), tx('relCLimit', 'RELCLimit'), tx('rehCLimit', 'REUCLimit'),
  tx('rpdSigFig', 'RPDSigFig'), tx('rpdCutOff', 'RPDCutOff'), tx('rpdDecimal', 'RPDDecimal'),
  tx('recSigFig', 'RecSigFig'), tx('recCutOff', 'RecCutOff'), tx('recDecimal', 'RecDecimal'),
];

/** G5 column lists, per tab, in G5's order. */
export const TAB_COLUMNS: Record<Exclude<TabId, 'component'>, ParamCol[]> = {
  sample: [
    ro('parameter', 'Parameter', 170), tx('synonym', 'Synonym', 110), ro('surrogateLink', 'Surrogate', 110),
    ro('internalStandardLink', 'InternalStandard', 120), num('sort', 'Sort', 70),
    tx('defaultResult', 'DefaultResult'), unit('defaultUnits', 'DefaultUnits'),
    tx('finalDefaultResult', 'FinalDefaultResult', 120), unit('finalDefaultUnits', 'FinalDefaultUnits'),
    tx('rptLimit', 'RL'), tx('mdl', 'MDL'), num('mdlSpikeAmount', 'MDLSpikeAmount', 110), unit('mdlSpikeUnits', 'MDLSpikeUnits'),
    tx('rsd', 'RSD'), tx('regulatoryLimit', 'RegulatoryLimit', 110), tx('mcl', 'MCL'), tx('loq', 'LOQ'), tx('uql', 'UQL'),
    tx('lowCLimit', 'LCLimit'), tx('highCLimit', 'UCLimit'), tx('relCLimit', 'RELCLimit'), tx('rehCLimit', 'REUCLimit'),
    tx('sigFig', 'SigFig'), tx('cutOff', 'CutOff'), tx('decimal', 'Decimal'), tx('comment', 'Comment', 160),
    tx('accreditation', 'Accrediation', 130), { field: 'retireDate', header: 'RetireDate', width: 110, edit: 'date' },
  ],
  surrogate: [
    ro('parameter', 'Parameter', 170), num('sort', 'Sort', 70), { field: 'internalStandardLink', header: 'InternalStandard', width: 130, edit: 'is' },
    num('spikeAmount', 'SpikeAmount'), unit('spikeAmountUnit', 'SpikeAmountUnits'), num('surrogateAmount', 'SurrogateAmount', 110),
    tx('surrogateLowLimit', 'SurrogateLLimit', 110), tx('surrogateHighLimit', 'SurrogateULimit', 110),
    tx('recLCLimit', 'RecLCLimit'), tx('recHCLimit', 'RecUCLimit'), tx('mdl', 'MDL'), tx('mcl', 'MCL'),
    tx('lowCLimit', 'LCLimit'), tx('highCLimit', 'UCLimit'), tx('relCLimit', 'RELCLimit'), tx('rehCLimit', 'REUCLimit'),
    tx('sigFig', 'SigFig'), tx('cutOff', 'CutOff'), tx('decimal', 'Decimal'),
    tx('recSigFig', 'RecSigFig'), tx('recCutOff', 'RecCutOff'), tx('recDecimal', 'RecDecimal'), tx('comment', 'Comment', 160),
  ],
  is: [
    ro('parameter', 'Parameter', 170), num('sort', 'Sort', 70), num('spikeAmount', 'SpikeAmount'), unit('spikeAmountUnit', 'SpikeAmountUnits'),
    tx('rpdHCLimit', 'RPDCtrlLimit'), tx('mdl', 'MDL'), tx('mcl', 'MCL'), tx('lowCLimit', 'LowCLimit'), tx('highCLimit', 'UCLimit'),
    tx('relCLimit', 'RELCLimit'), tx('rehCLimit', 'REUCLimit'), tx('sigFig', 'SigFig'), tx('cutOff', 'CutOff'), tx('decimal', 'Decimal'),
    tx('comment', 'Comment', 160),
  ],
  qc: [
    ro('qcType', 'QCType', 110), ro('parameter', 'Parameter', 170), tx('synonym', 'Synonym', 110), num('sort', 'Sort', 70), ...qcLimits,
    tx('comment', 'Comment', 160), { field: 'internalStandardLink', header: 'InternalStandardLink', width: 130, edit: 'is' },
  ],
  qcDefault: [
    ro('qcType', 'QCType', 110), ro('parameter', 'Parameter', 170), tx('synonym', 'Synonym', 110), num('sort', 'Sort', 70), ...qcLimits,
    tx('sigFig', 'SigFig'), tx('cutOff', 'CutOff'), tx('decimal', 'Decimal'), tx('comment', 'Comment', 160),
  ],
};

/** The tabs in G5's order. `kind` = what ADD creates there (none on QC Parameter Defaults, as in G5). */
export const PARAM_TABS: { id: TabId; title: string; kind?: ParamKind }[] = [
  { id: 'sample', title: 'Sample Parameters', kind: 'sample' },
  { id: 'surrogate', title: 'Surrogates', kind: 'surrogate' },
  { id: 'is', title: 'Internal Standards', kind: 'is' },
  { id: 'qc', title: 'QC Sample Parameters', kind: 'qc' },
  { id: 'component', title: 'Component' },
  { id: 'qcDefault', title: 'QC Parameter Defaults' },
];

const isQc = (r: ParamRow) => !!r.qcType && r.qcType !== 'Sample';

/** G5's filter for each tab (SettingFormCriteriaViewController / the list view criteria). */
export function rowsOfTab(rows: GridRow[], tab: TabId): GridRow[] {
  const sortThenName = (a: GridRow, b: GridRow) =>
    (Number(a.sort ?? 0) - Number(b.sort ?? 0)) || String(a.parameter ?? '').localeCompare(String(b.parameter ?? ''));
  const byName = (a: GridRow, b: GridRow) => String(a.parameter ?? '').localeCompare(String(b.parameter ?? ''));
  switch (tab) {
    case 'sample': return rows.filter((r) => r.qcType === 'Sample' && r.componentIsDefault && !r.surrogate && !r.internalStandard).sort(sortThenName);
    case 'surrogate': return rows.filter((r) => r.surrogate).sort(sortThenName);
    case 'is': return rows.filter((r) => r.internalStandard).sort(sortThenName);
    // QC Sample Parameters: EVERY QC (non-Sample) row of the test, by QC type then name. G5 shows one QC type
    // at a time through a combo; this window lists them all, with a QCType column (owner request 2026-10-16).
    case 'qc': return rows.filter(isQc).sort((a, b) => String(a.qcType).localeCompare(String(b.qcType)) || byName(a, b));
    case 'qcDefault': return rows.filter(isQc).sort((a, b) => String(a.qcType).localeCompare(String(b.qcType)) || byName(a, b));
    default: return [];
  }
}

/** The wire key a cell is saved under: a lookup column saves its Oid. */
export const wireKey = (col: ParamCol) =>
  col.edit === 'unit' || col.edit === 'is' ? `${col.field}Id` : col.field;

/** The ADD / edit buffer that OK sends (POST …/parameters/save). */
export interface PendingChanges {
  adds: { kind: ParamKind; parameterId: string; qcTypeId: string | null; values: Record<string, string | null> }[];
  edits: { id: string; values: Record<string, string | null> }[];
}

export function buildSave(rows: GridRow[], edits: Record<string, Record<string, string | null>>): PendingChanges {
  const adds = rows.filter((r) => r.pending).map((r) => ({
    kind: r.pending!.kind, parameterId: r.parameterId!, qcTypeId: r.pending!.qcTypeId, values: edits[r.key] ?? {},
  }));
  const saved = rows.filter((r) => !r.pending && edits[r.key] && Object.keys(edits[r.key]!).length > 0)
    .map((r) => ({ id: r.oid, values: edits[r.key]! }));
  return { adds, edits: saved };
}

/** A new row for a parameter picked in ADD — shown at once, written on OK. */
export function pendingRow(seq: number, kind: ParamKind, p: { id: string; name: string | null }, qc: { id: string; name: string } | null): GridRow {
  return {
    key: `new-${seq}`, oid: '', parameterId: p.id, parameter: p.name, sort: 0,
    qcTypeId: kind === 'sample' ? null : qc?.id ?? null,
    qcType: kind === 'sample' ? 'Sample' : kind === 'qc' ? qc?.name ?? null : null,
    componentIsDefault: true, surrogate: kind === 'surrogate', internalStandard: kind === 'is', hasDefaultResults: false,
    pending: { kind, qcTypeId: kind === 'qc' ? qc?.id ?? null : null },
  };
}

/** The cell edit as the grid shows it and as it is sent (lookups send the Oid). */
export function cellEdit(col: ParamCol, value: string, units: Option[], isRows: GridRow[]): { wire: Record<string, string | null>; display: Record<string, unknown> } {
  const v = value.trim();
  if (col.edit === 'unit') {
    const u = units.find((x) => x.name === v);
    return { wire: { [wireKey(col)]: u?.id ?? null }, display: { [col.field]: u?.name ?? null } };
  }
  if (col.edit === 'is') {
    const r = isRows.find((x) => x.parameter === v);
    return { wire: { [wireKey(col)]: r?.oid ?? null }, display: { [col.field]: r?.parameter ?? null } };
  }
  return { wire: { [col.field]: v === '' ? null : v }, display: { [col.field]: v === '' ? null : v } };
}
